import React, { useState, useMemo } from "react";
import data from "./data";
import Button from "../Buttons/Button";
import SmartTooltip from "../Info/SmartTooltip";
import { getDailyProductionStart } from "../../redux/slices/leads/leadsSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import FormFieldSet from "../FormFields/FormFieldSet";
import { useLoginUser } from "../../hooks/useLoginUser";
import Backdrop from "../Backgrounds/Backdrop";
import LogoLoader from "../Loading/LogoLoader";
import { getEndOfDay, getStartOfDay } from "../../utils/common";
import CustomDatePicker from "../FormFields/Custom/CustomDatePicker";
import { formatWords2 } from "../../utils/stringUtils";
// import employeeGroups from "./employeeGroups.json";

// Centralized Style System
const baseStyles = {
  cell: "px-3 py-1 font-normal text-sm h-[44px] border border-gray-300 flex items-center border-r-0 border-b-0",
  headerCell:
    "px-3 py-1 font-normal text-sm h-[54px] border border-gray-300 flex items-center border-r-0 ",
  lastRowCell: "border-b-2 border-b-blue-500",
};

const cellStyles = {
  // Header styles
  header: `${baseStyles.headerCell} text-gray-500 bg-gray-100`,
  headerWeekend: `${baseStyles.headerCell} text-gray-500 bg-gray-200`,
  headerSummary: `${baseStyles.headerCell} bg-blue-100 flex justify-center text-center px-5 !text-xs`,
  fixedHeader: `${baseStyles.headerCell} text-gray-500 bg-gray-100 sticky left-0 z-30`,

  // Body cell styles
  bodyCell: `${baseStyles.cell} text-black bg-white`,
  bodyCellWeekend: `${baseStyles.cell} text-black bg-gray-100`,
  bodyCellSummary: `${baseStyles.cell} text-black bg-white`,
  fixedBodyCell: `${baseStyles.cell} sticky left-0 z-20`,

  //total Cell Style
  footerMain: `${baseStyles.cell} text-gray-100 bg-primary sticky left-0 z-30`,
  footer: `${baseStyles.cell} text-dark bg-primary-50`,
  footerSummary: `${baseStyles.cell} text-dark bg-primary-50 flex justify-center text-center px-5`,
  footerWeekend: `${baseStyles.cell} text-gray-500 bg-gray-200`,
};

const layoutStyles = {
  // Main container
  gridContainer: "w-full flex flex-1 bg-white relative",

  // Section wrappers
  sectionsWrapper: "flex flex-col w-full",
  headerSection: "sticky top-0 z-40 bg-white shadow-sm",
  managerSection: "",
  teamSection: "shadow-sm",

  // Grid layouts
  gridRow: "grid min-w-max",
  headerRow: "grid min-w-max sticky top-0 z-40 bg-white",

  // Spacing
  sectionGap: "mb-2",
  teamMemberGap: "",
};

// Color variants for different employee types
const employeeStyles = {
  manager: "bg-blue-500 text-white",
  teamLead: "bg-success-300 text-black border-b-1 border-b-white",
  member: "bg-success-100 text-black border-b-1 border-b-white",
};

const DailyProductionTable = () => {
  const { userData: loginUserData } = useLoginUser();
  const userRoleType = loginUserData?.role?.type;
  const level = loginUserData?.level;
  const [isCompare, setIsCompare] = useState(false);
  const [openList, setOpenList] = useState([]); // It is used in comparision
  //api  data
  const dispatch = useDispatch();
  const dailyproduction = useSelector(
    (state) => state.leads.dailyProduction.data,
  );
  const { loading } = useSelector((state) => state?.leads);
  // Helper functions
  const getFirstDayOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month + 1, 0);
  };

  const getToday = () => {
    return new Date();
  };

  const [presetRange, setPresetRange] = useState({
    from: getFirstDayOfCurrentMonth(),
    to: new Date(),
  });

  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [selectedUsers, setSelectedUsers] = useState([]);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Helper function to check if a date is weekend
  const isWeekend = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00"); // Force local timezone
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  };

  // FIXED: Consistent date formatting function
  const formatDateToString = (date) => {
    if (!date) return null;

    // Ensure we're working with a Date object
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    // Get components in local timezone to avoid timezone shifts
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // DatePicker onChange handler
  const handleDateRangeChange = (dateRange) => {
    console.log("Date Range Selected: ", dateRange);

    if (dateRange && dateRange.from && dateRange.to) {
      setPresetRange(dateRange);
      setDisplayYear(dateRange.from.getFullYear());
    }
  };

  // PERFORMANCE OPTIMIZATION 1: Memoize filtered data
  const filteredProductivityData = useMemo(() => {
    return data.productivityData.filter((item) => {
      const itemDate = new Date(item.date + "T00:00:00"); // Force local timezone
      return itemDate >= presetRange.from && itemDate <= presetRange.to;
    });
  }, [data.productivityData, presetRange.from, presetRange.to]);

  // FIXED: Simplified lookup map with consistent date format
  // const productivityLookup = useMemo(() => {
  //   const lookup = {};

  //   filteredProductivityData.forEach((item) => {
  //     // Use the date as-is from the data (it's already in YYYY-MM-DD format)
  //     const key = `${item.employeeId}-${item.date}`;
  //     lookup[key] = item.productivityValue;
  //   });

  //   return lookup;
  // }, [filteredProductivityData]);
  const productivityLookup = useMemo(() => {
    const lookup = {};

    dailyproduction.data?.forEach((manager) => {
      // Manager dailyData
      (manager.aggData.dateWise || []).forEach((item) => {
        const key = `${manager.uiid}-${item.addedOn}`;
        lookup[key] = item.totalCompanies || 0;
      });

      if (Array.isArray(manager.teamMembers)) {
        manager.teamMembers.forEach((lead) => {
          // Team lead dailyData
          (lead.aggData.dateWise || []).forEach((item) => {
            const key = `${lead.uiid}-${item.addedOn}`;
            lookup[key] = item.totalCompanies || 0;
          });

          // Team lead members dailyData
          (lead.hierarchy || []).forEach((member) => {
            (member.aggData.dateWise || []).forEach((item) => {
              const key = `${member.uiid}-${item.addedOn}`;
              lookup[key] = item.totalCompanies || 0;
            });
          });
        });
      }
    });

    return lookup;
  }, [dailyproduction]);

  // PERFORMANCE OPTIMIZATION 3: Pre-calculate employee totals
  // const employeeTotals = useMemo(() => {
  //   const totals = {};
  //   filteredProductivityData.forEach((item) => {
  //     if (!totals[item.employeeId]) {
  //       totals[item.employeeId] = {
  //         totalCompanies: 0,
  //         totalLeads: Math.floor(Math.random() * 50) + 20,
  //         jobPositive: Math.floor(Math.random() * 30) + 10,
  //         jobOpenings: Math.floor(Math.random() * 25) + 5,
  //         jobClosed: Math.floor(Math.random() * 15) + 3,
  //       };
  //     }
  //     totals[item.employeeId].totalCompanies += item.productivityValue;
  //   });
  //   return totals;
  // }, [filteredProductivityData]);

  // FIXED: Consistent date calculations
  const selectedMonthDates = useMemo(() => {
    const dates = [];
    const startDate = new Date(presetRange.from);
    const endDate = new Date(presetRange.to);

    // Create dates in local timezone to avoid shifts
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = formatDateToString(d);
      dates.push({
        date: d.getDate(),
        day: d.toLocaleDateString("en-US", { weekday: "short" }),
        dateStr: dateStr,
        fullDate: new Date(d),
        isWeekend: isWeekend(dateStr),
      });
    }

    return dates;
  }, [presetRange.from, presetRange.to]);

  // NEW: Get employees to display (filtered by selectedUsers if any)
  // const employeesToDisplay = useMemo(() => {
  //   const allEmployees = data.employees.filter(
  //     (emp) => emp.role !== "RA Manager"
  //   );

  //   if (selectedUsers.length === 0) {
  //     return allEmployees; // Show all if none selected
  //   }

  //   return allEmployees.filter((emp) => selectedUsers.includes(emp.id));
  // }, [data.employees, selectedUsers]);
  const allEmployees = useMemo(() => {
    if (!dailyproduction?.data) return [];

    const employees = [];

    dailyproduction.data.forEach((manager) => {
      // Manager
      employees.push({
        id: manager.uiid,
        name: `${manager.firstName} ${manager.lastName}`,
        role: "Manager",
      });

      // Team Leads
      manager.teamMembers?.forEach((lead) => {
        employees.push({
          id: lead.uiid,
          name: `${lead.firstName} ${lead.lastName}`,
          role: "Team Lead",
        });

        // RAs under Team Lead
        lead.hierarchy?.forEach((member) => {
          employees.push({
            id: member.uiid,
            name: `${member.firstName} ${member.lastName}`,
            role: "RA",
          });
        });
      });
    });

    return employees;
  }, [dailyproduction]);

  // const getEmployeeTotals = (employeeId) => {
  //   return (
  //     employeeTotals[employeeId] || {
  //       totalCompanies: 0,
  //       totalLeads: 0,
  //       jobPositive: 0,
  //       jobOpenings: 0,
  //       jobClosed: 0,
  //     }
  //   );
  // };

  // Create user options for CustomSelect
  // const userOptions = useMemo(() => {
  //   return data.employees
  //     .filter((emp) => emp.role !== "RA Manager") // Exclude manager from selection
  //     .map((emp) => ({
  //       value: emp.id,
  //       label: `${emp.name} - ${emp.role}`,
  //     }));
  // }, [data.employees]);
  const userOptions = useMemo(() => {
    return allEmployees.map((emp) => ({
      value: emp.id,
      label: `${formatWords2(emp.name)}`,
    }));
  }, [allEmployees]);

  // Handle user selection change
  // const handleUserSelectionChange = (selectedUserIds) => {
  //   setSelectedUsers(selectedUserIds || []);
  // };

  // Event handlers
  const handlePrevYear = () => setDisplayYear(displayYear - 1);

  const handleNextYear = () => {
    const currentYear = getToday().getFullYear();
    if (displayYear < currentYear) {
      setDisplayYear(displayYear + 1);
    }
  };

  const isNextYearDisabled = () => displayYear >= getToday().getFullYear();

  const isMonthDisabled = (monthIndex) => {
    const today = getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    return (
      displayYear > currentYear ||
      (displayYear === currentYear && monthIndex > currentMonth)
    );
  };

  const isMonthSelected = (monthIndex) => {
    const fromYear = presetRange.from.getFullYear();
    const toYear = presetRange.to.getFullYear();
    const fromMonth = presetRange.from.getMonth();
    const toMonth = presetRange.to.getMonth();

    if (fromYear !== toYear) {
      if (displayYear === fromYear) {
        return monthIndex >= fromMonth;
      } else if (displayYear === toYear) {
        return monthIndex <= toMonth;
      } else if (displayYear > fromYear && displayYear < toYear) {
        return true;
      }
      return false;
    }

    if (displayYear === fromYear) {
      return monthIndex >= fromMonth && monthIndex <= toMonth;
    }

    return false;
  };

  const handleMonthChange = (monthIndex) => {
    const today = getToday();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const startDate = new Date(displayYear, monthIndex, 1);
    let endDate;

    if (displayYear === currentYear && monthIndex === currentMonth) {
      endDate = today;
    } else if (
      displayYear > currentYear ||
      (displayYear === currentYear && monthIndex > currentMonth)
    ) {
      return;
    } else {
      endDate = getLastDayOfMonth(displayYear, monthIndex);
    }

    const newRange = { from: startDate, to: endDate };
    setPresetRange(newRange);
  };

  //   return { raManager, teamStructure };
  // }, [dailyproduction, selectedUsers]);
  // const employeeGroups = useMemo(() => {
  //   if (!dailyproduction?.data || !Array.isArray(dailyproduction.data)) {
  //     return { raManager: [], teamStructure: [] };
  //   }

  //   const selectedUserSet = new Set(selectedUsers); // fast lookup
  //   const raManager = [];
  //   const teamStructure = [];

  //   dailyproduction.data.forEach((manager) => {
  //     let includeManager =
  //       selectedUsers.length === 0 || selectedUserSet.has(manager.uiid);

  //     const managerTeamStructure = [];

  //     manager.teamMembers?.forEach((lead) => {
  //       // include lead if selected or if any of their members are selected
  //       const filteredMembers =
  //         lead.hierarchy
  //           ?.filter(
  //             (member) =>
  //               selectedUsers.length === 0 || selectedUserSet.has(member.uiid)
  //           )
  //           .map((member) => ({
  //             id: member.uiid,
  //             name: `${member.firstName} ${member.lastName}`,
  //             totalCompanies: member.aggData.totalCompanies,
  //             totalLeads: member.aggData.totalProspects,
  //             jobPositive: member.aggData.totalJobPositive,
  //             jobOpenings: member.aggData.totalJobOpening,
  //             jobClosed: member.aggData.totalJobClosed,
  //           })) || [];

  //       // const includeLead =
  //       //   selectedUsers.length === 0 ||
  //       //   selectedUserSet.has(lead.uiid) ||
  //       //   filteredMembers.length > 0;
  //       // include lead if manager is selected OR lead is selected OR any member is selected
  //       const includeLead =
  //         selectedUserSet.has(manager.uiid) ||
  //         selectedUserSet.has(lead.uiid) ||
  //         filteredMembers.length > 0 ||
  //         selectedUsers.length === 0;

  //       if (includeLead) {
  //         managerTeamStructure.push({
  //           teamLead: {
  //             id: lead.uiid,
  //             name: `${lead.firstName} ${lead.lastName}`,
  //             reportingManager: manager.uiid,
  //             totalCompanies: lead.aggData.totalCompanies,
  //             totalLeads: lead.aggData.totalProspects,
  //             jobPositive: lead.aggData.totalJobPositive,
  //             jobOpenings: lead.aggData.totalJobOpening,
  //             jobClosed: lead.aggData.totalJobClosed,
  //           },
  //           members: filteredMembers,
  //         });
  //         includeManager = true; // ensure manager row shows if any child is included
  //       }
  //     });

  //     if (includeManager) {
  //       raManager.push({
  //         id: manager.uiid,
  //         name: `${manager.firstName} ${manager.lastName}`,
  //         totalCompanies: manager.aggData.totalCompanies,
  //         totalLeads: manager.aggData.totalProspects,
  //         jobPositive: manager.aggData.totalJobPositive,
  //         jobOpenings: manager.aggData.totalJobOpening,
  //         jobClosed: manager.aggData.totalJobClosed,
  //       });

  //       teamStructure.push(...managerTeamStructure);
  //     }
  //   });

  //   return { raManager, teamStructure };
  // }, [dailyproduction, selectedUsers]);
  const employeeGroups = useMemo(() => {
    if (!dailyproduction?.data || !Array.isArray(dailyproduction.data)) {
      return { raManager: [], teamStructure: [] };
    }

    const selectedUserSet = new Set(selectedUsers);
    const raManager = [];
    const teamStructure = [];

    dailyproduction.data.forEach((manager) => {
      let includeManager =
        selectedUsers.length === 0 || selectedUserSet.has(manager.uiid);
      const managerTeamStructure = [];

      // Process team members and their hierarchies
      manager.teamMembers?.forEach((lead) => {
        // Filter members based on selection
        const filteredMembers =
          lead.hierarchy
            ?.filter(
              (member) =>
                selectedUsers.length === 0 ||
                selectedUserSet.has(manager.uiid) ||
                selectedUserSet.has(lead.uiid) ||
                selectedUserSet.has(member.uiid),
            )
            .map((member) => ({
              id: member.uiid,
              name: `${member.firstName} ${member.lastName}`,
              totalCompanies: member.aggData?.totalCompanies || 0,
              totalLeads: member.aggData?.totalProspects || 0,
              jobPositive: member.aggData?.totalJobPositive || 0,
              jobOpenings: member.aggData?.totalJobOpening || 0,
              jobClosed: member.aggData?.totalJobClosed || 0,
            })) || [];

        const includeLead =
          selectedUserSet.has(manager.uiid) ||
          selectedUserSet.has(lead.uiid) ||
          filteredMembers.length > 0 ||
          selectedUsers.length === 0;

        if (includeLead) {
          managerTeamStructure.push({
            teamLead: {
              id: lead.uiid,
              name: `${formatWords2(lead.firstName)} ${formatWords2(lead.lastName)}`,
              reportingManager: manager.uiid,
              totalCompanies: lead.aggData?.totalCompanies || 0,
              totalLeads: lead.aggData?.totalProspects || 0,
              jobPositive: lead.aggData?.totalJobPositive || 0,
              jobOpenings: lead.aggData?.totalJobOpening || 0,
              jobClosed: lead.aggData?.totalJobClosed || 0,
            },
            members: filteredMembers,
          });
          includeManager = true;
        }
      });

      if (includeManager) {
        raManager.push({
          id: manager.uiid,
          name: `${manager.firstName} ${manager.lastName}`,
          totalCompanies: manager.aggData?.totalCompanies || 0,
          totalLeads: manager.aggData?.totalProspects || 0,
          jobPositive: manager.aggData?.totalJobPositive || 0,
          jobOpenings: manager.aggData?.totalJobOpening || 0,
          jobClosed: manager.aggData?.totalJobClosed || 0,
        });

        teamStructure.push(...managerTeamStructure);
      }
    });

    return { raManager, teamStructure };
  }, [dailyproduction, selectedUsers]);

  const compareProductivity = useMemo(() => {
    const compare = {};
    const teamLeadsProductivity = {};

    employeeGroups.teamStructure.map((grp) =>
      selectedMonthDates.map((date) => {
        const key = `${grp.teamLead.id}-${date.dateStr}`;
        let value = productivityLookup[key];

        grp.members.map((mem) => {
          const memKey = `${mem.id}-${date.dateStr}`;
          const memValue = productivityLookup[memKey];
          value = value + memValue;
        });

        compare[key] = value;
        teamLeadsProductivity[grp.teamLead.id] = value;
      }),
    );

    employeeGroups.raManager.map((manager) => {
      const managerReporties = employeeGroups.teamStructure.filter(
        (lead) => lead.teamLead.reportingManager === manager.id,
      );
      selectedMonthDates.map((date) => {
        const managerKey = `${manager.id}-${date.dateStr}`;
        let managerValue = productivityLookup[managerKey];

        managerReporties.map((mrepo) => {
          const reportKey = `${mrepo.teamLead.id}-${date.dateStr}`;
          managerValue = managerValue + compare[reportKey];
        });

        compare[managerKey] = managerValue;
      });
    });

    return compare;
  }, [employeeGroups, selectedMonthDates]);

  // Helper: empty totals object
  // const emptyTotals = () => ({
  //   totalCompanies: 0,
  //   totalLeads: 0,
  //   jobPositive: 0,
  //   jobOpenings: 0,
  //   jobClosed: 0,
  // });

  // // Helper: add two totals safely
  // const addTotals = (a, b) => ({
  //   totalCompanies: a.totalCompanies + b.totalCompanies,
  //   totalLeads: a.totalLeads + b.totalLeads,
  //   jobPositive: a.jobPositive + b.jobPositive,
  //   jobOpenings: a.jobOpenings + b.jobOpenings,
  //   jobClosed: a.jobClosed + b.jobClosed,
  // });

  // const getCompareEmployeeTotals = (employeeId, employeeType) => {
  //   let compareTotals = emptyTotals();

  //   if (employeeType === "manager") {
  //     // Manager's own totals
  //     compareTotals = employeeTotals[employeeId] || emptyTotals();

  //     // All team leads reporting to this manager
  //     const teamLeads = employeeGroups.teamStructure.filter(
  //       (lead) => lead?.teamLead?.reportingManager === employeeId
  //     );

  //     teamLeads.forEach((lead) => {
  //       // Team lead totals
  //       const leadTotals = employeeTotals[lead.teamLead.id] || emptyTotals();
  //       compareTotals = addTotals(compareTotals, leadTotals);

  //       // Members under each team lead
  //       lead.members.forEach((mem) => {
  //         const memTotals = employeeTotals[mem.id] || emptyTotals();
  //         compareTotals = addTotals(compareTotals, memTotals);
  //       });
  //     });
  //   } else if (employeeType === "teamLead") {
  //     // Team lead’s own totals
  //     compareTotals = employeeTotals[employeeId] || emptyTotals();

  //     // Find this team lead in structure
  //     const leadReports = employeeGroups.teamStructure.filter(
  //       (lead) => lead.teamLead.id === employeeId
  //     );

  //     if (leadReports.length > 0) {
  //       leadReports[0].members.forEach((mem) => {
  //         const memTotals = employeeTotals[mem.id] || emptyTotals();
  //         compareTotals = addTotals(compareTotals, memTotals);
  //       });
  //     }
  //   }

  //   return compareTotals;
  // };

  //   const compareEmployeeTotals = useMemo(() => {
  //     let compareTotals = {};

  //     employeeGroups.teamStructure.map((grp) => {
  //       let leadTotals = employeeTotals[grp.teamLead.id] || {
  //         totalCompanies: 0,
  //         totalLeads: 0,
  //         jobPositive: 0,
  //         jobOpenings: 0,
  //         jobClosed: 0,
  //       };

  //       grp.members.map((mem) => {
  //         const memTotals = employeeTotals[mem.id] || {
  //           totalCompanies: 0,
  //           totalLeads: 0,
  //           jobPositive: 0,
  //           jobOpenings: 0,
  //           jobClosed: 0,
  //         };
  //         leadTotals.totalCompanies += memTotals.totalCompanies;
  //         leadTotals.totalLeads += memTotals.totalLeads;
  //         leadTotals.jobPositive += memTotals.jobPositive;
  //         leadTotals.jobOpenings += memTotals.jobOpenings;
  //         leadTotals.jobClosed += memTotals.jobClosed;
  //       });

  //       compareTotals[grp.teamLead.id] = leadTotals;
  //     });

  //     employeeGroups.raManager.map((manager) => {
  //       let managerTotals = employeeTotals[manager.id] || {
  //         totalCompanies: 0,
  //         totalLeads: 0,
  //         jobPositive: 0,
  //         jobOpenings: 0,
  //         jobClosed: 0,
  //       };
  //       const managerReporties = employeeGroups.teamStructure.filter(
  //         (lead) => lead.teamLead.reportingManager === manager.id
  //       );

  //       managerReporties.map((mrepo) => {
  //         const mrepoTotals = compareTotals[mrepo.teamLead.id];
  //         managerTotals.totalCompanies += mrepoTotals.totalCompanies;
  //         managerTotals.totalLeads += mrepoTotals.totalLeads;
  //         managerTotals.jobPositive += mrepoTotals.jobPositive;
  //         managerTotals.jobOpenings += mrepoTotals.jobOpenings;
  //         managerTotals.jobClosed += mrepoTotals.jobClosed;
  //       });

  //       compareTotals[manager.id] = managerTotals;
  //     });

  //     return compareTotals;
  //   }, [employeeGroups, employeeTotals]);

  // FIXED: Simplified lookup function

  // const getEmployeeProductivityByDate = (employeeId, dateStr) => {
  //   if (!dateStr || !employeeId) return 0;

  //   const key = `${employeeId}-${dateStr}`;
  //   const value = productivityLookup[key];

  //   return value !== undefined ? value : 0;
  // };
  const getEmployeeProductivityByDate = (employeeId, dateStr) => {
    if (!dateStr || !employeeId) return 0;
    const key = `${employeeId}-${dateStr}`;
    return productivityLookup[key] || 0;
  };
  // const getCompareProductivityByDate = (employeeId, dateStr) => {
  //   if (!dateStr || !employeeId) return 0;

  //   const key = `${employeeId}-${dateStr}`;
  //   const value = compareProductivity[key];

  //   return value !== undefined ? value : 0;
  // };
  const getCompareProductivityByDate = (employeeId, dateStr) => {
    if (!dateStr || !employeeId) return 0;
    const key = `${employeeId}-${dateStr}`;
    const value = compareProductivity[key];
    return Number(value) || 0; // safe conversion
  };

  // Calculate grid template columns
  const gridCols = `220px repeat(${selectedMonthDates.length}, 44px) repeat(5, minmax(96px, 1fr))`;

  const GridHeader = () => (
    <div className={`${layoutStyles.headerSection} mb-2`}>
      <div
        className={layoutStyles.headerRow}
        style={{ gridTemplateColumns: gridCols }}
      >
        <div className={`${cellStyles.fixedHeader} text-left`}>RA Names</div>
        {selectedMonthDates.map((d, index) => (
          <div
            key={index}
            className={`${
              d.isWeekend ? cellStyles.headerWeekend : cellStyles.header
            } justify-center`}
          >
            <div className="flex flex-col gap-1 text-dark text-xs">
              <span className="font-medium leading-none text-center">
                {new Date(`${d.dateStr}T00:00:00`).toLocaleString("en-US", {
                  month: "short",
                })}
              </span>
              <span className="text-primary font-medium leading-none text-center">
                {d.date}
              </span>
              <span className="font-medium leading-none text-center">
                {d.day}
              </span>
            </div>
          </div>
        ))}
        <div className={`${cellStyles.headerSummary}`}>No of Companies</div>
        <div className={`${cellStyles.headerSummary}`}>No of Leads</div>
        <div className={`${cellStyles.headerSummary}`}>Job Positives</div>
        <div className={`${cellStyles.headerSummary}`}>Job Openings</div>
        <div className={`${cellStyles.headerSummary}`}>Job Closed</div>
      </div>
    </div>
  );

  const GridRow = ({
    employee,
    employeeType = "member",
    isLastInTeam = false,
  }) => {
    const displayName =
      employeeType === "manager"
        ? `${employee.name} (Manager)`
        : employeeType === "teamLead"
          ? `${employee.name} (Team Lead)`
          : employee.name;

    const rowBorderClass = isLastInTeam ? "" : layoutStyles.teamMemberGap;
    const lastRowCell = isLastInTeam ? "border-b-[1px] !border-gray-300" : "";

    return (
      <div
        className={`${layoutStyles.gridRow} ${rowBorderClass}`}
        style={{ gridTemplateColumns: gridCols }}
      >
        <div
          className={`${cellStyles.fixedBodyCell} ${
            employeeStyles[employeeType]
          } ${lastRowCell} font-medium ${
            isCompare && employeeType === "manager" ? `cursor-pointer` : ``
          } flex justify-between`}
          onClick={() => {
            if (isCompare && employeeType === "manager") {
              setOpenList((prev) =>
                prev.includes(employee?.id)
                  ? prev.filter((id) => id !== employee?.id)
                  : [...prev, employee?.id],
              );
            }
          }}
        >
          <span className="line-clamp-1">{displayName}</span>

          {isCompare && employeeType === "manager" && (
            <span
              className={`material-icons transition-transform ml-1 flex-shrink-0`}
            >
              {openList.includes(employee?.id) ? "expand_less" : "expand_more"}
            </span>
          )}
        </div>

        {selectedMonthDates.map((d, index) => (
          <div
            key={index}
            className={`${
              d.isWeekend ? cellStyles.bodyCellWeekend : cellStyles.bodyCell
            } ${lastRowCell} justify-center`}
          >
            {isCompare
              ? getCompareProductivityByDate(employee.id, d.dateStr)
              : getEmployeeProductivityByDate(employee.id, d.dateStr)}
          </div>
        ))}

        {/* FIXED: These now use the actual employee data from API */}
        <div
          className={`${cellStyles.bodyCellSummary} ${lastRowCell} justify-center font-medium`}
        >
          {employee.totalCompanies || 0}
        </div>
        <div
          className={`${cellStyles.bodyCellSummary} ${lastRowCell} justify-center font-medium`}
        >
          {employee.totalLeads || 0}
        </div>
        <div
          className={`${cellStyles.bodyCellSummary} ${lastRowCell} justify-center font-medium`}
        >
          {employee.jobPositive || 0}
        </div>
        <div
          className={`${cellStyles.bodyCellSummary} ${lastRowCell} justify-center font-medium`}
        >
          {employee.jobOpenings || 0}
        </div>
        <div
          className={`${cellStyles.bodyCellSummary} ${lastRowCell} justify-center font-medium`}
        >
          {employee.jobClosed || 0}
        </div>
      </div>
    );
  };
  // Calculate column totals for each date
  const columnTotals = useMemo(() => {
    const totals = {};

    selectedMonthDates.forEach((dateObj) => {
      let dateTotal = 0;

      employeeGroups.teamStructure.forEach(({ teamLead, members }) => {
        dateTotal += getEmployeeProductivityByDate(
          teamLead.id,
          dateObj.dateStr,
        );
        members.forEach((member) => {
          dateTotal += getEmployeeProductivityByDate(
            member.id,
            dateObj.dateStr,
          );
        });
      });

      totals[dateObj.dateStr] = dateTotal;
    });

    employeeGroups.raManager.map((manager) =>
      selectedMonthDates.forEach((dateObj) => {
        let dateTotal = 0;

        dateTotal += getEmployeeProductivityByDate(manager.id, dateObj.dateStr);
        totals[dateObj.dateStr] += dateTotal;
      }),
    );

    return totals;
  }, [selectedMonthDates, employeeGroups, getEmployeeProductivityByDate]);

  // Calculate grand totals for summary columns
  const grandTotals = useMemo(() => {
    let totalCompanies = 0;
    let totalLeads = 0;
    let jobPositive = 0;
    let jobOpenings = 0;
    let jobClosed = 0;

    if (employeeGroups.raManager) {
      employeeGroups.raManager.map((manager) => {
        //const managerTotals = getEmployeeTotals(manager.id);
        totalCompanies += manager.totalCompanies;
        totalLeads += manager.totalLeads;
        jobPositive += manager.jobPositive;
        jobOpenings += manager.jobOpenings;
        jobClosed += manager.jobClosed;
      });
    }

    employeeGroups.teamStructure.forEach(({ teamLead, members }) => {
      // const teamLeadTotals = getEmployeeTotals(teamLead.id);
      totalCompanies += teamLead.totalCompanies;
      totalLeads += teamLead.totalLeads;
      jobPositive += teamLead.jobPositive;
      jobOpenings += teamLead.jobOpenings;
      jobClosed += teamLead.jobClosed;

      members.forEach((member) => {
        //const memberTotals = getEmployeeTotals(member.id);
        totalCompanies += member.totalCompanies;
        totalLeads += member.totalLeads;
        jobPositive += member.jobPositive;
        jobOpenings += member.jobOpenings;
        jobClosed += member.jobClosed;
      });
    });

    return {
      totalCompanies,
      totalLeads,
      jobPositive,
      jobOpenings,
      jobClosed,
    };
  }, [employeeGroups]);

  const ColumnTotalGridRow = () => {
    return (
      <div className={`${layoutStyles.headerSection}`}>
        <div
          className={layoutStyles.headerRow}
          style={{ gridTemplateColumns: gridCols }}
        >
          <div className={`${cellStyles.footerMain} text-left font-bold`}>
            Total Count
          </div>
          {selectedMonthDates.map((d, index) => (
            <div
              key={index}
              className={`${
                d.isWeekend ? cellStyles.footerWeekend : cellStyles.footer
              } justify-center font-bold border-b-[1px]`}
            >
              {columnTotals[d.dateStr] || 0}
            </div>
          ))}
          <div
            className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
          >
            {grandTotals.totalCompanies}
          </div>
          <div
            className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
          >
            {grandTotals.totalLeads}
          </div>
          <div
            className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
          >
            {grandTotals.jobPositive}
          </div>
          <div
            className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
          >
            {grandTotals.jobOpenings}
          </div>
          <div
            className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
          >
            {grandTotals.jobClosed}
          </div>
        </div>
      </div>
    );
  };
  const action = {
    useAggregate: true,
    lim: 10,
    coll: "Company",
    queryMode: "AG_DAILYPRODUCTION",
    fil: JSON.stringify({
      dates: {
        type: "AG_DAILYPRODUCTION",
        // value: ["2025-09-18T18:30:00.000Z", "2025-09-30T18:30:00.000Z"],
        // value: [presetRange.from, presetRange.to],
        value: [
          // formatDateToString(presetRange.from),
          // formatDateToString(presetRange.to),
          // convertToUTC(dayjs(presetRange.from).startOf("day")),
          // convertToUTC(dayjs(presetRange.to).endOf("day")),
          getStartOfDay(presetRange.from),
          getEndOfDay(presetRange.to),
        ],
        userId: userRoleType !== 1 && level !== 5 ? loginUserData?.uiid : "",
      },
    }),
  };

  //chnage1
  useEffect(() => {
    dispatch(getDailyProductionStart(action));
  }, [dispatch, presetRange]);

  return (
    <div className="w-full h-full flex flex-col gap-2 overflow-hidden">
      {/* Toolbar - Fixed at top */}
      <div className="toolbar flex gap-2 justify-between items-center flex-shrink-0 px-3 pt-3">
        <div className="w-[200px]">
          <FormFieldSet
            type="multiSelect"
            noTitle
            options={userOptions}
            value={selectedUsers}
            onChange={(val) => setSelectedUsers(val)}
            size="sm"
            placeholder="ALL RA List"
            minwidth="min-w-48"
            showSelectAll={false}
            showSearch
          />
        </div>
        <div className="flex gap-2 items-center">
          {/* <div className="buttonCalendar flex gap-1 items-center justify-between">
            <Button
              title={`${displayYear - 1}`}
              icon={"west"}
              size="sm"
              color="black"
              onClick={handlePrevYear}
            />
            {monthNames.map((month, index) => (
              <Button
                key={index}
                title={month}
                size="sm"
                variant={isMonthSelected(index) ? "contained" : "outlined"}
                color={isMonthSelected(index) ? "primary" : "black"}
                onClick={() => handleMonthChange(index)}
                disabled={isMonthDisabled(index)}
              />
            ))}
            <Button
              title={`${displayYear + 1}`}
              icon={"east"}
              iconPosition="after"
              size="sm"
              color="black"
              onClick={handleNextYear}
              disabled={isNextYearDisabled()}
            />
          </div> */}
          <div>
            <CustomDatePicker
              range={true}
              size="sm"
              selected={presetRange}
              onChange={handleDateRangeChange}
              maxDate={new Date()}
              //dropdownOptions="all"
            />
          </div>
          {/* <div className="w-[150px]">
            <DatePicker
              size="sm"
              range
              selected={presetRange}
              onChange={handleDateRangeChange}
              maxDate={new Date()}
            />
          </div> */}
          {Array.isArray(selectedUsers) && selectedUsers?.length == 0 && <SmartTooltip description={"Compare"} tooltipPosition="bottom" noicon>
            <Button
              type="icon"
              icon={"compare"}
              variant="text"
              color={`${isCompare ? `primary` : `black`}`}
              onClick={() => setIsCompare(!isCompare)}
            />
          </SmartTooltip>}
        </div>
      </div>

      {/* Table Section - Scrollable */}
      <div className="flex-1 min-h-0 px-3 pb-3">
        <div className="h-full overflow-auto bg-white rounded border border-gray-200">
          <div className=" min-w-max flex flex-col w-full relative">
            {/* Header - Sticky */}
            <div className="sticky top-0 z-40 bg-white shadow-sm mb-2">
              <div
                className="grid min-w-max"
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className={`${cellStyles.fixedHeader} text-left`}>
                  RA Names
                </div>
                {selectedMonthDates.map((d, index) => (
                  <div
                    key={index}
                    className={`${
                      d.isWeekend ? cellStyles.headerWeekend : cellStyles.header
                    } justify-center`}
                  >
                    <div className="flex flex-col gap-1 text-dark text-xs">
                      <span className="font-medium leading-none text-center">
                        {new Date(`${d.dateStr}T00:00:00`).toLocaleString(
                          "en-US",
                          { month: "short" },
                        )}
                      </span>
                      <span className="text-primary font-medium leading-none text-center">
                        {d.date}
                      </span>
                      <span className="font-medium leading-none text-center">
                        {d.day}
                      </span>
                    </div>
                  </div>
                ))}
                <div className={`${cellStyles.headerSummary}`}>
                  No of Companies
                </div>
                <div className={`${cellStyles.headerSummary}`}>No of Leads</div>
                <div className={`${cellStyles.headerSummary}`}>
                  Job Positives
                </div>
                <div className={`${cellStyles.headerSummary}`}>
                  Job Openings
                </div>
                <div className={`${cellStyles.headerSummary}`}>Job Closed</div>
              </div>
            </div>

            {/* Employee Rows - Scrollable Content */}
            {(() => {
              if (selectedUsers.length === 0) {
                return (
                  <React.Fragment>
                    {employeeGroups.raManager &&
                      employeeGroups.raManager.map((manager) => (
                        <div key={manager.id}>
                          <div className="shadow-sm bg-amber-300">
                            <GridRow
                              employee={manager}
                              employeeType="manager"
                              isLastInTeam={true}
                            />
                          </div>

                          {employeeGroups.teamStructure
                            .filter(
                              (val) =>
                                val?.teamLead?.reportingManager === manager?.id,
                            )
                            .map(({ teamLead, members }, teamIndex) => (
                              <div
                                key={teamLead.id}
                                className={`mt-2 shadow-sm ${
                                  teamIndex <
                                  employeeGroups.teamStructure.length - 1
                                    ? "mb-2"
                                    : ""
                                }`}
                              >
                                {isCompare &&
                                  openList.includes(manager?.id) && (
                                    <GridRow
                                      employee={teamLead}
                                      employeeType="teamLead"
                                      isLastInTeam={isCompare ? true : false}
                                    />
                                  )}

                                {!isCompare && (
                                  <GridRow
                                    employee={teamLead}
                                    employeeType="teamLead"
                                    isLastInTeam={isCompare ? true : false}
                                  />
                                )}

                                {!isCompare &&
                                  members.map((member, memberIndex) => (
                                    <GridRow
                                      key={member.id}
                                      employee={member}
                                      employeeType="member"
                                      isLastInTeam={
                                        memberIndex === members?.length - 1
                                      }
                                    />
                                  ))}
                              </div>
                            ))}
                        </div>
                      ))}
                  </React.Fragment>
                );
              }

              const selectedUserSet = new Set(selectedUsers);
              const selectedManagers = employeeGroups.raManager.filter(
                (manager) => selectedUserSet.has(manager?.id),
              );
              const selectedTeamLeads = employeeGroups.teamStructure.filter(
                (team) =>
                  selectedUserSet.has(team?.teamLead?.id) &&
                  !selectedUserSet.has(team?.teamLead?.reportingManager),
              );
              const selectedMembers = employeeGroups.teamStructure.flatMap(
                (team) =>
                  team.members.filter(
                    (member) =>
                      selectedUserSet.has(member?.id) &&
                      !selectedUserSet.has(team?.teamLead?.id) &&
                      !selectedUserSet.has(team?.teamLead?.reportingManager),
                  ),
              );

              return (
                <React.Fragment>
                  {selectedManagers.map((manager) => (
                    <div key={manager.id}>
                      <div className="shadow-sm">
                        <GridRow
                          employee={manager}
                          employeeType="manager"
                          isLastInTeam={true}
                        />
                      </div>

                      {employeeGroups.teamStructure
                        .filter(
                          (team) =>
                            team.teamLead.reportingManager === manager.id,
                        )
                        .map(({ teamLead, members }, teamIndex) => (
                          <div
                            key={teamLead.id}
                            className={`mt-2 shadow-sm ${
                              teamIndex <
                              employeeGroups.teamStructure.length - 1
                                ? "mb-2"
                                : ""
                            }`}
                          >
                            <GridRow
                              employee={teamLead}
                              employeeType="teamLead"
                              isLastInTeam={members.length === 0}
                            />

                            {members.map((member, memberIndex) => (
                              <GridRow
                                key={member.id}
                                employee={member}
                                employeeType="member"
                                isLastInTeam={
                                  memberIndex === members?.length - 1
                                }
                              />
                            ))}
                          </div>
                        ))}
                    </div>
                  ))}

                  {selectedTeamLeads.map(({ teamLead, members }) => (
                    <div key={teamLead.id}>
                      <div className="shadow-sm">
                        <GridRow
                          employee={teamLead}
                          employeeType="teamLead"
                          isLastInTeam={members.length === 0}
                        />
                      </div>

                      {members.map((member, memberIndex) => (
                        <GridRow
                          key={member.id}
                          employee={member}
                          employeeType="member"
                          isLastInTeam={memberIndex === members.length - 1}
                        />
                      ))}
                    </div>
                  ))}

                  {selectedMembers.map((member) => (
                    <div key={member.id}>
                      <div className="shadow-sm">
                        <GridRow
                          employee={member}
                          employeeType="member"
                          isLastInTeam={true}
                        />
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })()}

            {/* Column Total Row - Sticky at bottom of scroll */}
            <div className="sticky bottom-0 z-30 bg-white shadow-sm">
              <div
                className="grid min-w-max"
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className={`${cellStyles.footerMain} text-left font-bold`}>
                  Total Count
                </div>
                {selectedMonthDates.map((d, index) => (
                  <div
                    key={index}
                    className={`${
                      d.isWeekend ? cellStyles.footerWeekend : cellStyles.footer
                    } justify-center font-bold border-b-[1px]`}
                  >
                    {columnTotals[d.dateStr] || 0}
                  </div>
                ))}
                <div
                  className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
                >
                  {grandTotals.totalCompanies}
                </div>
                <div
                  className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
                >
                  {grandTotals.totalLeads}
                </div>
                <div
                  className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
                >
                  {grandTotals.jobPositive}
                </div>
                <div
                  className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
                >
                  {grandTotals.jobOpenings}
                </div>
                <div
                  className={`${cellStyles.footerSummary} font-bold border-b-[1px]`}
                >
                  {grandTotals.jobClosed}
                </div>
              </div>
            </div>

            {selectedUsers.length > 0 &&
              employeeGroups.raManager.length === 0 &&
              employeeGroups.teamStructure.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No employees found for the selected users.
                </div>
              )}
          </div>
        </div>
      </div>

      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default DailyProductionTable;